import { observer } from 'mobx-react-lite';
import { FC } from 'react';

import { IcCheckbox16Icon, IcClose16Icon } from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';
import { Button } from '@gem/ui-kit';

import { ConfirmationType } from 'APP/model/calendar/calendar.types';
import { User } from 'APP/store/Users/User/User';

import { IEventCalendar } from '../../Content.types';
import { CallLink } from '../Form/CallLink/CallLink';
import { CalendarInfoDescription } from './CalendarInfoDescription/CalendarInfoDescription';
import { useInfoPresenter } from './Info.presenter';
import styles from './Info.styles.m.css';
import { Participants } from './Participants';

interface IInfoProps {
  selectedEvent: IEventCalendar;
  myEventStatus: ConfirmationType;
  updateStatus(confirmationType: ConfirmationType): Promise<void>;
  participantsInfo: User[];
}

export const Info: FC<IInfoProps> = observer((props) => {
  const { selectedEvent, updateStatus, myEventStatus, participantsInfo } = props;
  const presenter = useInfoPresenter({ selectedEvent });
  const { t } = useTranslation();

  return (
    <div className={styles.container}>
      <div
        className={styles.title}
        data-test="calendar-event-selected-title-text"
      >
        <div className={styles.colorContainer}>
          <div className={styles.circle} />
        </div>
        {selectedEvent.title}
      </div>
      <div
        className={styles.time}
        data-test="calendar-event-selected-time-text"
      >
        {presenter.time}
      </div>
      <div className={styles.repeat}>{presenter.repeatText}</div>
      <div className={styles.reminder}>{presenter.reminderText}</div>
      {presenter.canEditStatus && (
        <div className={styles.status}>
          <Button
            onClick={(): void => {
              updateStatus(ConfirmationType.ACCEPTED);
            }}
            Icon={IcCheckbox16Icon}
            iconClassName={styles.accept}
            isActive={myEventStatus === ConfirmationType.ACCEPTED}
            theme="secondary"
            size="10"
            rounding="1"
          >
            {t('calendar_action_accept')}
          </Button>
          <Button
            onClick={(): void => {
              updateStatus(ConfirmationType.DECLINED);
            }}
            Icon={IcClose16Icon}
            iconClassName={styles.decline}
            isActive={myEventStatus === ConfirmationType.DECLINED}
            theme="secondary"
            size="10"
            rounding="1"
          >
            {t('calendar_action_decline')}
          </Button>
          <Button
            onClick={(): void => {
              updateStatus(ConfirmationType.MAYBE);
            }}
            isActive={myEventStatus === ConfirmationType.MAYBE}
            theme="secondary"
            size="10"
            rounding="1"
          >
            {t('calendar_action_maybe')}
          </Button>
        </div>
      )}
      {selectedEvent.description && (
        <CalendarInfoDescription description={selectedEvent.description} />
      )}
      {selectedEvent.callLink && <CallLink link={selectedEvent.callLink} />}
      <Participants
        items={selectedEvent.participants}
        providerEmail={selectedEvent.account.providerEmail}
        initiatorEmail={selectedEvent.initiatorEmail}
        itemsInfo={participantsInfo}
      />
    </div>
  );
});
